import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from 'react';
import {VendorContext} from '../../context/VendorProvider';
import {AccountsContext} from '../../context/AccountsProvider';
import debounce from 'lodash/debounce';
import {FaLink, FaUnlink, FaCheck, FaUserShield} from 'react-icons/fa';

const LinkedAccounts = ({vendorId}) => {
  const {getVendors, linkVendors, unLinkVendors, getLinkedVendors} =
    useContext(VendorContext);
  const {updateAccounts, linkedAccounts, getLinkedAccounts} =
    useContext(AccountsContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [vendors, setVendors] = useState([]);
  const [linkedVendors, setLinkedVendors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const [unlinkAccountId, setUnlinkAccountId] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedVendorForUsers, setSelectedVendorForUsers] = useState(null);
  const [checkedAccounts, setCheckedAccounts] = useState([]);
  const wrapperRef = useRef(null);
  const userModalRef = useRef();
  const dropdownRef = useRef(null);
  const [showItems, setShowItems] = useState(false);

  useEffect(() => {
    const debouncedSearch = debounce(async () => {
      if (searchTerm.trim() === '') {
        try {
          const allVendors = await getVendors();
          const filteredVendors = allVendors.filter(
            (vendor) =>
              (vendor.linkedGroup === null ||
                (vendor.linkedGroup && vendor.linkedGroup.length < 2)) &&
              vendor.rowKey !== vendorId
          );
          setVendors(filteredVendors);
        } catch (error) {
          console.error('Failed to fetch vendors:', error);
        }
      } else {
        try {
          const allVendors = await getVendors();
          const filteredVendors = allVendors.filter(
            (vendor) =>
              (vendor.licensee
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()) ||
                vendor.vendorCode
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())) &&
              (vendor.linkedGroup === null ||
                (vendor.linkedGroup && vendor.linkedGroup.length < 2)) &&
              vendor.rowKey !== vendorId
          );
          setVendors(filteredVendors);
        } catch (error) {
          console.error('Error during vendor search:', error);
          setVendors([]);
        }
      }
    }, 300);

    debouncedSearch();
    return () => debouncedSearch.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, vendorId]);

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  const openUserModal = (vendorRowKey) => {
    setAccounts(
      linkedAccounts.filter(
        (account) => account.username && account.username !== 'Not Activated'
      )
    );
    setSelectedVendorForUsers(vendorRowKey);
    setShowUserModal(true);
  };

  const closeUserModal = () => {
    setShowUserModal(false);
  };

  const openUnlinkModal = (accountId) => {
    setShowUnlinkModal(true);
    setUnlinkAccountId(accountId);
  };

  const closeUnlinkModal = () => {
    setShowUnlinkModal(false);
    setUnlinkAccountId(null);
  };

  const handleVendorSelection = (e, vendorRowKey) => {
    e.stopPropagation();
    setSelectedVendors((prevSelected) => {
      if (prevSelected.includes(vendorRowKey)) {
        return prevSelected.filter((key) => key !== vendorRowKey);
      } else {
        return [...prevSelected, vendorRowKey];
      }
    });
    setIsDropdownOpen(true);
  };

  const handleSubmitSelectedVendors = async () => {
    setIsDropdownOpen(false);
    if (vendorId && selectedVendors.length > 0) {
      try {
        for (const vendorRowKey of selectedVendors) {
          const formData = {newLinkedRowKey: vendorRowKey};
          await linkVendors(vendorId, formData);
        }
        const updatedLinkedVendors = await getLinkedVendors(vendorId);
        setLinkedVendors(updatedLinkedVendors || []);
        setSelectedVendors([]);
        getLinkedAccounts(vendorId);
      } catch (error) {
        console.error('Error linking vendors:', error);
      }
    }
  };

  const handleUnlinkVendor = async () => {
    if (unlinkAccountId) {
      try {
        await unLinkVendors(vendorId, [unlinkAccountId]);
        setLinkedVendors((prevVendors) =>
          prevVendors.filter((vendor) => vendor.rowKey !== unlinkAccountId)
        );
        closeUnlinkModal();
      } catch (error) {
        console.error('Error unlinking vendor:', error);
      }
    }
  };

  const handleCheckboxChange = useCallback(
    (event, account) => {
      var newAllowedVendors = new Set(account.allowedVendors);
      if (event.target.checked) {
        newAllowedVendors.add(selectedVendorForUsers);
      } else {
        newAllowedVendors.delete(selectedVendorForUsers);
      }
      account.allowedVendors = Array.from(newAllowedVendors);
      setAccounts(
        accounts.map((a) => {
          if (a.id === account.id) {
            return account;
          } else {
            return a;
          }
        })
      );

      setCheckedAccounts((prev) => {
        if (!prev.includes(account.id)) {
          prev.push(account.id);
        }
        return prev;
      });
    },
    [selectedVendorForUsers, setAccounts, accounts]
  );

  const handleGrantAccountsPermissions = async () => {
    const accountsToUpdate = accounts.filter((account) =>
      checkedAccounts.includes(account.id)
    );
    await updateAccounts(accountsToUpdate);
    setCheckedAccounts([]);
    closeUserModal();
  };

  const sortedLinkedVendors = [...linkedVendors].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) {
      return -1;
    } else if (!a.isPrimary && b.isPrimary) {
      return 1;
    } else {
      return 0;
    }
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showUserModal &&
        userModalRef.current &&
        !userModalRef.current.contains(event.target)
      ) {
        setShowUserModal(false);
        setCheckedAccounts([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserModal]);

  // useEffect(() => {
  //   getLinkedAccounts(vendorId).catch(error => console.error("Error fetching accounts:", error));
  // }, [vendorId, getLinkedAccounts]);

  useEffect(() => {
    setCheckedAccounts([]);
  }, [linkedAccounts, selectedVendorForUsers]);

  useEffect(() => {
    setLinkedVendors([]);
    setAccounts([]);
    const fetchVendorData = async () => {
      try {
        const linkedVendorsData = await getLinkedVendors(vendorId);
        setLinkedVendors(linkedVendorsData || []);

        const linkedAccountsData = await getLinkedAccounts(vendorId);
        setAccounts(linkedAccountsData || []);
      } catch (error) {
        console.error('Failed to fetch data for vendor:', error);
      }
    };

    fetchVendorData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  // useEffect(() => {
  //   const fetchAllVendors = async () => {
  //     try {
  //       const allVendors = await getVendors();
  //       const linkedVendorRowKeys = linkedVendors.map(vendor => vendor.rowKey);
  //       const filteredVendors = allVendors.filter(vendor =>
  //         !linkedVendorRowKeys.includes(vendor.rowKey) &&
  //         (vendor.linkedGroup === null || (vendor.linkedGroup && vendor.linkedGroup.length < 2)) &&
  //         vendor.rowKey !== vendorId
  //       );
  //       setVendors(filteredVendors || []);
  //     } catch (error) {
  //       console.error("Failed to fetch vendors:", error);
  //     }
  //   };
  //   fetchAllVendors();
  // }, [vendorId, linkedVendors]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowItems(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="rounded-lg bg-white p-4 dark:bg-stone-900"
    >
      <div className="relative mb-4 flex">
        <input
          type="text"
          className="block w-full rounded-md border border-gray-300 bg-gray-100 px-2 py-1.5 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
          placeholder="Search Vendors..."
          value={searchTerm}
          onFocus={handleInputFocus}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoComplete="off"
        />
        <button
          onClick={handleSubmitSelectedVendors}
          className="ml-2 w-40 rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-700"
        >
          Link Vendor
        </button>
        {isDropdownOpen && vendors.length > 0 && (
          <ul
            ref={dropdownRef}
            className="absolute z-10 mt-12 max-h-60 w-full overflow-auto rounded border border-gray-200 bg-white dark:border-stone-700 dark:bg-stone-800 dark:text-gray-300"
          >
            {showItems &&
              vendors.map((vendor) => (
                <li
                  className="flex cursor-pointer items-center justify-between p-2 hover:bg-gray-200 dark:hover:bg-stone-600"
                  onClick={(e) => handleVendorSelection(e, vendor.rowKey)}
                >
                  <div className="flex items-center">
                    <div className="mr-2 flex h-5 w-5 items-center justify-center">
                      {selectedVendors.includes(vendor.rowKey) && (
                        <FaCheck className="link-check-icon text-green-500"/>
                      )}
                    </div>
                    <span>
                      {vendor.licensee} ({vendor.vendorCode})
                    </span>
                  </div>
                </li>
              ))}
          </ul>
        )}
      </div>
      {linkedVendors.length > 0 && (
        <div className="mt-8">
          <div className="mt-4 overflow-x-auto">
            <div
              className="relative overflow-y-auto h-[650px]"
            >
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-left text-sm text-gray-500 dark:text-gray-300">
                <thead
                  className="sticky top-0 bg-white text-xs uppercase text-gray-700 dark:bg-stone-900 dark:text-gray-300">
                <tr>
                  <th scope="col" className="py-3 pl-6">
                    Vendor Name
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Actions
                  </th>
                </tr>
                </thead>
                <tbody>
                {sortedLinkedVendors.map(
                  (vendor) =>
                    vendor.rowKey !== vendorId && (
                      <tr
                        className={` ${
                          isDropdownOpen
                            ? 'bg-gray-200 opacity-50 hover:bg-gray-200 dark:border-stone-900 dark:bg-stone-950 dark:hover:bg-stone-900'
                            : 'border-b bg-white hover:bg-gray-50 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-600'
                        } `}
                      >
                        <td
                          className={`px-6 py-2.5 ${isDropdownOpen ? 'text-gray-500 dark:text-gray-600' : ''}`}
                        >
                          {vendor.licensee ? vendor.licensee : ''}
                          {vendor.isPrimary === true && (
                            <span className="master-link-icon">
                            <FaLink
                              title={'Master Linked Vendor'}
                              style={{
                                color: 'gold',
                                marginLeft: '10px',
                                marginBottom: '2px',
                              }}
                              className="master-link-icon ml-2 inline-block h-3 w-3"
                            />
                            </span>
                          )}
                        </td>
                        <td
                          className={`flex items-center px-6 py-2.5 ${isDropdownOpen ? '' : ''}`}
                        >
                          {vendor.isPrimary !== true && (
                            <button
                              onClick={() => openUnlinkModal(vendor.rowKey)}
                              className={`danger-hollow ${isDropdownOpen ? 'border-red-900 text-red-900' : 'border-red-500 text-red-500'} mr-2 flex items-center`}
                            >
                              <FaUnlink className="unlink-icon mr-2 h-3 w-3"/>
                              <span>Unlink</span>
                            </button>
                          )}
                          {vendor.isPrimary !== true && (
                            <button
                              onClick={() => openUserModal(vendor.rowKey)}
                              className={`primary-hollow hover:text-white ${isDropdownOpen ? 'border-blue-900 text-blue-900 dark:border-indigo-900 dark:text-indigo-900' : 'border-blue-500 text-blue-500 dark:border-indigo-500 dark:text-indigo-500'} flex items-center`}
                            >
                              <FaUserShield className="user-shield-icon mr-2 h-3 w-3"/>
                              <span>Users</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {showUnlinkModal && (
        <div
          className="fixed inset-0 flex h-full w-full items-center justify-center overflow-y-auto bg-stone-950 bg-opacity-75"
          id="delete-modal"
        >
          <div className="relative mx-auto w-96 rounded-md bg-white p-5 shadow-lg dark:bg-stone-800">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                Unlink Vendor
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-300">
                  Are you sure you want to unlink this vendor?
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  id="delete-ok-btn"
                  className="rounded-md bg-red-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  onClick={handleUnlinkVendor}
                >
                  Unlink
                </button>
                <button
                  className="ml-3 rounded-md bg-gray-300 px-4 py-2 text-base font-medium text-gray-900 shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400"
                  onClick={closeUnlinkModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showUserModal && (
        <div
          className="fixed inset-0 flex h-full w-full items-center justify-center overflow-y-auto bg-stone-950 bg-opacity-75">
          <div
            ref={userModalRef}
            className="relative mx-auto w-96 rounded-md bg-white p-5 shadow-lg dark:bg-stone-800"
          >
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                Link Accounts
              </h3>
              <div className="mt-2 px-7 py-3">
                <ul className="max-h-[250px] overflow-y-scroll text-gray-900 dark:text-white">
                  {accounts.map((account) => (
                    <li className="flex items-center">
                      <input
                        type="checkbox"
                        checked={account.allowedVendors.includes(
                          selectedVendorForUsers
                        )}
                        onChange={(e) => handleCheckboxChange(e, account)}
                        disabled={account.vendor === selectedVendorForUsers}
                        id={`account-checkbox-${account.id}`}
                      />
                      <label
                        className="ml-2"
                        htmlFor={`account-checkbox-${account.id}`}
                      >
                        {account.username}{' '}
                        {account.parent ? '(sub account)' : ''}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  id="confirm-btn"
                  className="primary-small py-2"
                  onClick={handleGrantAccountsPermissions}
                >
                  Confirm
                </button>
                <button
                  className="ml-2 rounded bg-gray-500 px-4 py-2 font-semibold text-white hover:bg-gray-600"
                  onClick={closeUserModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkedAccounts;